import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ColDef,
    GridApi,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ICellRendererParams,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExtensionApiService } from 'src/app/core/api/extension-api.service';
import { ExtensionOverviewRow } from 'src/app/core/models/extension-overview-row';
import { ExtensionStatus } from 'src/app/core/models/extension';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { FillMissingDataModalComponent } from './components/fill-missing-data-modal/fill-missing-data-modal.component';

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
]);

/** Row plus a precomputed, translated "what's missing" summary for the grid. */
type ExtensionOverviewVm = ExtensionOverviewRow & { missingText: string };

@Component({
    selector: 'app-extensions-overview',
    standalone: true,
    imports: [AgGridAngular, FormsModule, TranslatePipe],
    templateUrl: './extensions-overview.component.html',
    styleUrl: './extensions-overview.component.scss',
})
export class ExtensionsOverviewComponent implements OnInit {
    public readonly NEEDS_ACTION = 'needsAction';

    // Statuses an admin can still act on. "Needs action" is limited to these; terminal
    // statuses (Confirmed/Declined/Failed/Cancelled) are only shown when picked explicitly.
    private readonly actionableStatuses = [
        ExtensionStatus.Draft,
        ExtensionStatus.InProgress,
        ExtensionStatus.AwaitingApproval,
    ];

    public allRows: ExtensionOverviewVm[] = [];
    public rowData: ExtensionOverviewVm[] = [];
    public colDefs: ColDef<ExtensionOverviewVm>[] = [];

    public selectedStatus: string = this.NEEDS_ACTION;
    // EndDate month filter (YYYY-MM) — same control and logic as currency-overview.
    public selectedMonth: string = '';

    public statusFilterOptions: { value: string; labelKey: string }[] = [
        { value: this.NEEDS_ACTION, labelKey: 'extensionsOverview.statusFilter.needsAction' },
        { value: String(ExtensionStatus.Draft), labelKey: 'extensionsOverview.status.Draft' },
        { value: String(ExtensionStatus.InProgress), labelKey: 'extensionsOverview.status.InProgress' },
        { value: String(ExtensionStatus.AwaitingApproval), labelKey: 'extensionsOverview.status.AwaitingApproval' },
        { value: String(ExtensionStatus.Confirmed), labelKey: 'extensionsOverview.status.Confirmed' },
        { value: String(ExtensionStatus.Declined), labelKey: 'extensionsOverview.status.Declined' },
        { value: String(ExtensionStatus.Failed), labelKey: 'extensionsOverview.status.Failed' },
        { value: String(ExtensionStatus.Cancelled), labelKey: 'extensionsOverview.status.Cancelled' },
    ];

    private gridApi: GridApi<ExtensionOverviewVm>;

    public defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

    // Confirmed extensions need no action — style them distinctly.
    public rowClassRules = {
        'confirmed-row': (params: any) => params.data?.status === ExtensionStatus.Confirmed,
    };

    constructor(
        private extensionApiService: ExtensionApiService,
        private translateService: TranslateService,
        private modalService: NgbModal,
        private router: Router,
        private spinner: SpinnerService,
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    private loadData(): void {
        this.spinner.show();
        this.extensionApiService.getOverview().subscribe({
            next: (rows) => {
                // Built here (not in ngOnInit) so translate.instant has loaded translations.
                this.allRows = rows.map((r) => ({ ...r, missingText: this.buildMissingText(r) }));
                this.buildColDefs();
                this.applyFilter();
                this.spinner.hide();
            },
            error: () => this.spinner.hide(),
        });
    }

    public onGridReady(params: any): void {
        this.gridApi = params.api;
        this.gridApi.setGridOption('rowData', this.rowData);
    }

    public onStatusFilterChange(): void {
        this.applyFilter();
    }

    public onMonthFilterChange(): void {
        this.applyFilter();
    }

    public clearMonthFilter(): void {
        this.selectedMonth = '';
        this.applyFilter();
    }

    private applyFilter(): void {
        let rows: ExtensionOverviewVm[];

        if (this.selectedStatus === this.NEEDS_ACTION) {
            // Default: incomplete extensions in an actionable status (not terminal ones).
            rows = this.allRows.filter(
                (r) => r.hasMissingData && this.actionableStatuses.includes(r.status)
            );
        } else {
            const statusNum = Number(this.selectedStatus);
            rows = this.allRows.filter((r) => r.status === statusNum);
        }

        // EndDate month filter — mirrors currency-overview applyFilter() exactly.
        if (this.selectedMonth) {
            const [year, month] = this.selectedMonth.split('-').map(Number);
            rows = rows.filter((r) => {
                if (!r.endDate) return false;
                const d = new Date(r.endDate);
                return d.getFullYear() === year && d.getMonth() + 1 === month;
            });
        }

        this.rowData = rows;
        this.gridApi?.setGridOption('rowData', this.rowData);
    }

    private buildMissingText(row: ExtensionOverviewRow): string {
        const t = this.translateService;
        const m = row.missing;
        if (!m || !m.hasMissingData) {
            return '';
        }
        if (m.noContractDetails) {
            return t.instant('extensionsOverview.summary.noContractDetails');
        }
        const parts: string[] = [];
        for (const d of m.details ?? []) {
            const seg: string[] = [];
            if (d.missingFields?.length) {
                seg.push(d.missingFields.map((f) => t.instant('extensionsOverview.field.' + f)).join(', '));
            }
            if (d.noItemNumbers) {
                seg.push(t.instant('extensionsOverview.summary.noItemNumbers'));
            }
            if (d.items?.length) {
                seg.push(t.instant('extensionsOverview.summary.itemsIncomplete', { count: d.items.length }));
            }
            if (seg.length) {
                parts.push(seg.join(', '));
            }
        }
        return parts.join(' · ');
    }

    private formatGridDate = (params: any): string =>
        params.value ? formatDate(params.value, 'dd-MM-yyyy', 'en') : '';

    private dateFilterGetter = (field: 'endDate') => (params: any): string =>
        params.data?.[field] ? formatDate(params.data[field], 'dd-MM-yyyy', 'en') : '';

    public openContract(row: ExtensionOverviewRow): void {
        this.router.navigate(['/contract', row.contractId, 'details']);
    }

    public openFillModal(row: ExtensionOverviewRow): void {
        const options: NgbModalOptions = { centered: true, backdrop: true, size: 'lg' };
        const modalRef = this.modalService.open(FillMissingDataModalComponent, options);
        modalRef.componentInstance.row = row;
        modalRef.result.then(
            (saved) => {
                if (saved) {
                    this.loadData();
                }
            },
            () => { /* dismissed — nothing to do */ }
        );
    }

    private buildColDefs(): void {
        const t = this.translateService;
        this.colDefs = [
            {
                field: 'customerName',
                headerName: t.instant('extensionsOverview.customerName'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 130,
            },
            {
                field: 'consultantName',
                headerName: t.instant('extensionsOverview.consultantName'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 130,
            },
            {
                colId: 'status',
                headerName: t.instant('extensionsOverview.statusColumn'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 130,
                valueGetter: (params: any) =>
                    params.data ? t.instant('extensionsOverview.status.' + params.data.statusName) : '',
            },
            {
                field: 'endDate',
                headerName: t.instant('extensionsOverview.endDate'),
                valueFormatter: this.formatGridDate,
                filterValueGetter: this.dateFilterGetter('endDate'),
                filter: 'agTextColumnFilter',
                flex: 1,
                minWidth: 110,
            },
            {
                field: 'missingText',
                headerName: t.instant('extensionsOverview.missingData'),
                filter: 'agTextColumnFilter',
                flex: 2,
                minWidth: 220,
                tooltipField: 'missingText',
            },
            {
                colId: 'actions',
                headerName: t.instant('extensionsOverview.actions'),
                filter: false,
                sortable: false,
                width: 230,
                cellRenderer: (params: ICellRendererParams<ExtensionOverviewVm>) => {
                    const container = document.createElement('div');
                    container.style.display = 'flex';
                    container.style.gap = '6px';
                    container.style.alignItems = 'center';
                    container.style.height = '100%';

                    if (params.data?.hasMissingData) {
                        const fill = document.createElement('button');
                        fill.className = 'btn btn-sm btn-primary';
                        fill.textContent = t.instant('extensionsOverview.fillIn');
                        fill.addEventListener('click', () => this.openFillModal(params.data!));
                        container.appendChild(fill);
                    }

                    const open = document.createElement('button');
                    open.className = 'btn btn-sm btn-outline-secondary';
                    open.textContent = t.instant('extensionsOverview.openContract');
                    open.addEventListener('click', () => this.openContract(params.data!));
                    container.appendChild(open);

                    return container;
                },
            },
        ];
    }
}
